import type { LoginInput, RegisterInput, UserPublic, UserSettings, UserStatsSummary } from '@en3/shared';
import { api, apiRequest } from './client';

/** 后端认证端点的返回结构。 */
export interface AuthSession {
  user: UserPublic;
  accessToken: string;
  expiresIn: number;
  refreshExpiresAt?: number;
}

export interface MeResponse {
  user: UserPublic;
  stats: UserStatsSummary;
}

/**
 * 认证接口封装。所有请求都经 `api/client`，因此自动带 Cookie、自动解信封、自动映射中文错误。
 * 注意 `refresh` 显式跳过"401 自动刷新"——它本身就是刷新，否则会递归。
 */
export const authApi = {
  register: (input: RegisterInput) => api.post<AuthSession>('/api/auth/register', input),
  login: (input: LoginInput) => api.post<AuthSession>('/api/auth/login', input),
  refresh: () => apiRequest<AuthSession>('/api/auth/refresh', { method: 'POST', skipRefreshRetry: true }),
  logout: (allDevices = false) =>
    api.post<{ loggedOut: boolean; allDevices: boolean }>(
      '/api/auth/logout',
      { allDevices },
      { skipRefreshRetry: true },
    ),
  me: () => api.get<MeResponse>('/api/me'),

  /** 更新设置（主题 / 阅读器字号行距 / 默认学习模式）。 */
  updateSettings: (patch: Partial<UserSettings>) =>
    api.patch<{ settings: UserSettings }>('/api/me/settings', patch),

  /**
   * 注销账号。
   *
   * `mode: 'soft'` 停用（数据保留，可恢复）；`mode: 'purge'` 真正删除个人数据。
   * 两者都必须显式传 `confirm: true` —— 后端用 zod 字面量校验，缺了会被拒。
   */
  deleteAccount: (mode: 'soft' | 'purge') =>
    api.delete<{ mode: 'soft' | 'purge'; revokedSessions: number; purged: { wordRecords: number } | null }>(
      '/api/me',
      { mode, confirm: true },
      { skipRefreshRetry: true },
    ),
};
